import { makeAutoObservable, runInAction } from 'mobx'
import { AxiosError } from 'axios'
import { uiStore } from 'shared/store/uiStore'
import { logger } from 'shared/lib'
import { toastStore } from 'shared/ui'
import { type IEntitiesStore } from 'entities/EntitiesExport'
import { ComplianceApi } from 'entities/Compliance'
import { Phone } from 'entities/Phone/model/Phone'
import { type InboxLayerStore } from 'pages/settings/pages/inboxes/pages/id/store/inboxLayerStore'
import { InboxesPath } from 'pages/settings/pages/inboxes/route/inboxesPath'
import { PaymentCommonStore } from 'pages/settings/ui/PaymentCommon/store/paymentCommonStore'
import { ChooseNumberStore } from 'pages/settings/store/newInboxSteps/chooseNumberStore'
import { InboxesRoutes } from 'pages/settings/pages/inboxes/route/type'

export enum BuyNumberRoutesSteps {
  ChooseNumber = 'choose-number',
  Payment = 'payment',
}

const titleMap: Record<string, string> = {
  [BuyNumberRoutesSteps.ChooseNumber]: 'Choose number',
  [BuyNumberRoutesSteps.Payment]: 'Payment',
}

export class BuyNumberStepsStore {
  private _currentStep = 1
  private _loading = false
  private _chooseNumberStore: ChooseNumberStore
  private readonly _paymentStore: PaymentCommonStore
  private _visibleUI = false

  constructor(private _inboxLayerStore: InboxLayerStore, private _entities: IEntitiesStore) {
    this._chooseNumberStore = new ChooseNumberStore(
      {
        nextClickHandler: this.nextClickHandler,
      },
      this._entities
    )
    this._paymentStore = new PaymentCommonStore(this.buyNumber, this._entities)

    makeAutoObservable(this)
  }

  init = async () => {
    this._entities.complianceStore.loadNumberVendors()
    await this._entities.subscriptionStore.init()
  }

  get inboxLayerStore() {
    return this._inboxLayerStore
  }

  get chooseNumberStore() {
    return this._chooseNumberStore
  }

  get paymentStore() {
    return this._paymentStore
  }

  get loading() {
    return this._loading
  }

  get inboxId() {
    return this._inboxLayerStore.inboxId
  }

  get rootPath() {
    return `${InboxesPath.inboxNumbers(this.inboxId)}/${InboxesRoutes.buyNumber}`
  }

  get availableLicensesCount() {
    return this._entities.subscriptionStore.licensesCount
  }

  get selectedNumbersCount() {
    return this._chooseNumberStore.chooseNumberTabsStore.selectedNumber ? 1 : 0
  }

  get requiredLicensesToBuy() {
    const numbersCount = this.selectedNumbersCount
    const availableLicenses = this.availableLicensesCount

    if (availableLicenses === 0) {
      return numbersCount
    }

    if (availableLicenses < numbersCount) {
      return numbersCount - availableLicenses
    }

    return 0
  }

  get stepMap() {
    return {
      1: BuyNumberRoutesSteps.ChooseNumber,
      2: BuyNumberRoutesSteps.Payment,
    }
  }

  get allStepsLength() {
    return Object.keys(this.stepMap).length
  }

  get getCurrentStep() {
    return this.getStep(0)
  }

  get getNextStep() {
    const step = this.getStep(1)
    if (!step) return this.getCurrentStep
    return step
  }

  get getLastStep() {
    const step = this.getStep(-1)
    if (!step) return this.getCurrentStep
    return step
  }

  getStep = (direction: -1 | 1 | 0) => {
    const allSteps = Object.values(this.stepMap)
    const currentPageIndex = this._currentStep - 1
    return allSteps[currentPageIndex + direction]
  }

  getSettingsStep = (path: BuyNumberRoutesSteps) => {
    return this.getStepByPath(path)
  }

  getStepByPath = (routeStep: BuyNumberRoutesSteps) => {
    const allPaths = Object.entries(this.stepMap)
    const stepIndex = allPaths.findIndex((path) => {
      return path[1] === routeStep
    })
    return stepIndex !== -1 ? stepIndex + 1 : 1
  }

  getProgressInfoTitle = (pathname: string) => {
    return titleMap[pathname] || ''
  }

  setCurrentStep = (step: number) => {
    this._currentStep = step
  }

  setVisibleUI = (status: boolean) => {
    this._visibleUI = status
  }

  nextClickHandler = () => {
    uiStore.changeRoute({
      path: `${this.rootPath}/${this.getNextStep}`,
    })
  }

  decreaseStep = () => {
    uiStore.changeRoute({
      path: `${this.rootPath}/${this.getLastStep}`,
    })
  }

  redirectToNumbers = async () => {
    uiStore.changeRoute({
      path: InboxesPath.inboxNumbers(this.inboxId),
    })
  }

  buyNumber = async () => {
    this.paymentStore.setPaymentError(false)
    if (!this.inboxId) return

    try {
      runInAction(() => {
        this._loading = true
      })

      const { selectedNumber, isUKSelected } = this._chooseNumberStore.chooseNumberTabsStore
      const number_vendor_id = this._entities.complianceStore.numberVendorId

      const { data } = await ComplianceApi.addTollFreeNumberToExistingInbox(this.inboxId, {
        members: null,
        name: '',
        number: selectedNumber?.number || '',
        number_vendor_id,
        owner: {
          id: null,
        },
        vendor_id: '',
        is_mobile: isUKSelected || null,
        payment_method: null,
      })

      await this._inboxLayerStore.init(this.inboxId)

      toastStore.add({
        type: 'success',
        title: 'Number added',
      })

      const addUseCaseLater = this.chooseNumberStore.selectedUseCaseId === -1
      const noUseCasesYet = this.chooseNumberStore.useCases.length === 0
      const noCompletedUseCases = this.chooseNumberStore.completedUseCases.length === 0

      if (data) {
        const targetNumber = data.find((item) => item.number === selectedNumber?.number)
        if (!targetNumber) return

        const needVerify =
          targetNumber.is_toll_free && (addUseCaseLater || noUseCasesYet || noCompletedUseCases)
        if (needVerify) {
          this._entities.complianceStore.setUnverifiedTollFreeNumber(
            new Phone(targetNumber, this._entities)
          )
        }

        const { selectedUseCaseId, useCaseNumberIds } = this.chooseNumberStore

        if (selectedUseCaseId !== -1 && selectedUseCaseId) {
          await ComplianceApi.submitTollFreeForReview(selectedUseCaseId, {
            numbers: useCaseNumberIds ? [...useCaseNumberIds, targetNumber.id] : [targetNumber.id],
            additional_numbers: [targetNumber.id],
          })
        }

        if (this._visibleUI) {
          this.redirectToNumbers()
        }

        if (needVerify && !this._entities.organizationStore.isTwilioNumberVendor) {
          setTimeout(() => {
            this._inboxLayerStore.inboxesViewStore.inboxesStore.settingsStore.openVerifyNumberModal(
              targetNumber,
              this._inboxLayerStore.currentTeam?.name || ''
            )
          }, 500)
        }
      }
    } catch (e) {
      logger.error(e)
      toastStore.add({
        type: 'error',
        title: 'Failed to add number',
      })

      if (e instanceof AxiosError && e.errors.getError('stripe') === 'Your card was declined.') {
        this.paymentStore.setPaymentError(true)
      }
    } finally {
      runInAction(() => {
        this._loading = false
      })
    }
  }

  dispose = () => {
    this._chooseNumberStore.chooseNumberTabsStore.resetInboxNumberSelection()
  }
}
